// Copyright 2021-2026 The Connect Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
var __await = (this && this.__await) || function (v) { return this instanceof __await ? (this.v = v, this) : new __await(v); }
var __asyncGenerator = (this && this.__asyncGenerator) || function (thisArg, _arguments, generator) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var g = generator.apply(thisArg, _arguments || []), i, q = [];
    return i = Object.create((typeof AsyncIterator === "function" ? AsyncIterator : Object).prototype), verb("next"), verb("throw"), verb("return", awaitReturn), i[Symbol.asyncIterator] = function () { return this; }, i;
    function awaitReturn(f) { return function (v) { return Promise.resolve(v).then(f, reject); }; }
    function verb(n, f) { if (g[n]) { i[n] = function (v) { return new Promise(function (a, b) { q.push([n, v, a, b]) > 1 || resume(n, v); }); }; if (f) i[n] = f(i[n]); } }
    function resume(n, v) { try { step(g[n](v)); } catch (e) { settle(q[0][3], e); } }
    function step(r) { r.value instanceof __await ? Promise.resolve(r.value.v).then(fulfill, reject) : settle(q[0][2], r); }
    function fulfill(value) { resume("next", value); }
    function reject(value) { resume("throw", value); }
    function settle(f, v) { if (f(v), q.shift(), q.length) resume(q[0][0], q[0][1]); }
};
import { Code, ConnectError, createContextValues } from "@connectrpc/connect";
import { compressedFlag, createClientMethodSerializers, createMethodUrl, encodeEnvelope, runStreamingCall, } from "@connectrpc/connect/protocol";
import { endStreamFlag, endStreamFromJson, requestHeader, } from "@connectrpc/connect/protocol-connect";
import { runWebTransportCall } from "./webtransport-helper.js";
const flagEnvelopeData = 0x00;
const flagEnvelopeHeaders = 0x06;
const flagEnvelopeReset = 0x07;
const streamIdLength = 4;
/**
 * One WebSocket connection carrying any number of concurrent RPC streams.
 * Every message is a 4-byte big-endian stream ID followed by one Connect
 * envelope; a single onmessage handler routes envelopes to the stream they
 * belong to. The socket is opened lazily and re-opened if it failed.
 */
class WebSocketMux {
    constructor(url, closeWhenIdle) {
        this.nextStreamId = 1;
        this.entries = new Map();
        this.url = url;
        this.closeWhenIdle = closeWhenIdle;
    }
    /**
     * Open a new stream on the connection, dialing it if necessary. The
     * returned readable yields the stream's incoming envelopes; each chunk
     * written to the writable must be exactly one envelope. Call
     * `closeStream` once the RPC is finished or abandoned.
     */
    async openStream() {
        const socket = await this.open();
        const streamId = this.nextStreamId++;
        // start() runs synchronously in the ReadableStream constructor, so the
        // controller is assigned before it is first used.
        let controller;
        const readable = new ReadableStream({
            start(readableController) {
                controller = readableController;
            },
        });
        this.entries.set(streamId, { controller, endSeen: false });
        const writable = new WritableStream({
            write: (chunk) => {
                socket.send(prefixStreamId(streamId, chunk));
            },
            close: () => {
                // Half-close: an explicit end-stream envelope, since neither the
                // stream nor the WebSocket has a send-direction close of its own.
                socket.send(prefixStreamId(streamId, encodeEnvelope(endStreamFlag, new Uint8Array())));
            },
            abort: () => {
                this.closeStream(streamId);
            },
        });
        return { streamId, readable, writable };
    }
    /**
     * Close the connection, terminating every stream on it. The mux remains
     * usable: the next `openStream` dials a new connection.
     */
    close() {
        const socket = this.socket;
        this.socket = undefined;
        this.failAll(new ConnectError("WebSocket closed", Code.Unavailable));
        socket === null || socket === void 0 ? void 0 : socket.close(1000);
    }
    /**
     * Release a stream. If the server hasn't finished it, a reset frame tells
     * it to stop work. With `closeWhenIdle`, the connection is closed once no
     * streams remain.
     */
    closeStream(streamId) {
        const entry = this.entries.get(streamId);
        if (entry === undefined) {
            return;
        }
        this.entries.delete(streamId);
        if (!entry.endSeen &&
            this.socket !== undefined &&
            this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(prefixStreamId(streamId, encodeEnvelope(flagEnvelopeReset, new Uint8Array())));
        }
        try {
            entry.controller.close();
        }
        catch (_a) {
            // The stream may already be closed or errored.
        }
        if (this.closeWhenIdle &&
            this.entries.size === 0 &&
            this.socket !== undefined) {
            this.socket.close(1000);
            this.socket = undefined;
        }
    }
    async open() {
        var _a;
        if (this.socket !== undefined &&
            this.socket.readyState === WebSocket.OPEN) {
            return this.socket;
        }
        (_a = this.opening) !== null && _a !== void 0 ? _a : (this.opening = new Promise((resolve, reject) => {
            const socket = new WebSocket(this.url);
            socket.binaryType = "arraybuffer";
            socket.onopen = () => {
                this.opening = undefined;
                this.socket = socket;
                resolve(socket);
            };
            socket.onerror = () => {
                this.opening = undefined;
                reject(new ConnectError("WebSocket connection failed", Code.Unavailable));
            };
            socket.onclose = (event) => {
                this.opening = undefined;
                if (this.socket === socket) {
                    this.socket = undefined;
                }
                // Surface the server's close reason (e.g. "idle timeout") so
                // callers can tell an expected disconnect from a failure.
                this.failAll(new ConnectError(event.reason !== ""
                    ? `WebSocket closed: ${event.reason}`
                    : "WebSocket closed", Code.Unavailable));
            };
            socket.onmessage = (event) => {
                this.route(new Uint8Array(event.data));
            };
        }));
        this.opening.catch(() => { });
        return this.opening;
    }
    /** Route one incoming message to the stream it belongs to. */
    route(message) {
        if (message.byteLength < streamIdLength + 1) {
            return;
        }
        const view = new DataView(message.buffer, message.byteOffset, message.byteLength);
        const streamId = view.getUint32(0);
        const flag = view.getUint8(streamIdLength);
        const entry = this.entries.get(streamId);
        if (entry === undefined) {
            // A late frame for a stream already closed on this side; drop it.
            return;
        }
        if (flag === flagEnvelopeReset) {
            this.entries.delete(streamId);
            try {
                entry.controller.error(new ConnectError("stream reset by server", Code.Canceled));
            }
            catch (_a) {
                // The stream may already be closed or errored.
            }
            return;
        }
        if (flag === endStreamFlag) {
            entry.endSeen = true;
        }
        entry.controller.enqueue(message.subarray(streamIdLength));
    }
    /** Terminate every stream after the connection failed or closed. */
    failAll(reason) {
        for (const entry of this.entries.values()) {
            try {
                entry.controller.error(reason);
            }
            catch (_a) {
                // The stream may already be closed or errored.
            }
        }
        this.entries.clear();
    }
}
function prefixStreamId(streamId, envelope) {
    const frame = new Uint8Array(streamIdLength + envelope.byteLength);
    new DataView(frame.buffer).setUint32(0, streamId);
    frame.set(envelope, streamIdLength);
    return frame;
}
export function createConnectWebSocketTransport(options) {
    var _a, _b;
    const useBinaryFormat = (_a = options.useBinaryFormat) !== null && _a !== void 0 ? _a : false;
    const url = new URL("/websocket", options.baseUrl);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = url.toString();
    const connectionPerStream = (_b = options.connectionPerStream) !== null && _b !== void 0 ? _b : false;
    // All RPCs share one multiplexed connection unless connectionPerStream is
    // set, in which case each RPC gets a mux of its own that closes when the
    // RPC finishes.
    const sharedMux = new WebSocketMux(wsUrl, false);
    return {
        close() {
            sharedMux.close();
        },
        async unary(_method, _signal, _timeoutMs, _header, _message, _contextValues) {
            throw new ConnectError("Unary not implemented over WS", Code.Unimplemented);
        },
        async stream(method, signal, timeoutMs, header, input, contextValues) {
            const { serialize, parse } = createClientMethodSerializers(method, useBinaryFormat, options.jsonOptions, options.binaryOptions);
            timeoutMs =
                timeoutMs === undefined
                    ? options.defaultTimeoutMs
                    : timeoutMs <= 0
                        ? undefined
                        : timeoutMs;
            return await runStreamingCall({
                interceptors: options.interceptors,
                timeoutMs,
                signal,
                req: {
                    stream: true,
                    service: method.parent,
                    method,
                    requestMethod: "POST",
                    url: createMethodUrl(options.baseUrl, method),
                    header: requestHeader(method.methodKind, useBinaryFormat, timeoutMs, header, false),
                    contextValues: contextValues !== null && contextValues !== void 0 ? contextValues : createContextValues(),
                    message: input,
                },
                next: async (req) => {
                    const mux = connectionPerStream
                        ? new WebSocketMux(wsUrl, true)
                        : sharedMux;
                    const { streamId, readable, writable } = await mux.openStream();
                    const path = new URL(req.url).pathname;
                    function rawMessageGenerator() {
                        return __asyncGenerator(this, arguments, function* rawMessageGenerator_1() {
                            var _a, e_1, _b, _c;
                            try {
                                for (var _d = true, _e = __asyncValues(req.message), _f; _f = yield __await(_e.next()), _a = _f.done, !_a; _d = true) {
                                    _c = _f.value;
                                    _d = false;
                                    const msg = _c;
                                    yield yield __await(serialize(msg));
                                }
                            }
                            catch (e_1_1) { e_1 = { error: e_1_1 }; }
                            finally {
                                try {
                                    if (!_d && !_a && (_b = _e.return)) yield __await(_b.call(_e));
                                }
                                finally { if (e_1) throw e_1.error; }
                            }
                        });
                    }
                    const { responseHeaders, responseMessages } = await runWebTransportCall({
                        ready: Promise.resolve(),
                        createBidirectionalStream: async () => ({
                            readable,
                            writable,
                        }),
                    }, req.header, rawMessageGenerator(), flagEnvelopeHeaders, flagEnvelopeData, path);
                    const responseTrailers = new Headers();
                    function iterate() {
                        return __asyncGenerator(this, arguments, function* iterate_1() {
                            var _a, e_2, _b, _c;
                            try {
                                try {
                                    for (var _d = true, responseMessages_1 = __asyncValues(responseMessages), responseMessages_1_1; responseMessages_1_1 = yield __await(responseMessages_1.next()), _a = responseMessages_1_1.done, !_a; _d = true) {
                                        _c = responseMessages_1_1.value;
                                        _d = false;
                                        const env = _c;
                                        if (env.flags === endStreamFlag) {
                                            const { error, metadata } = endStreamFromJson(env.data);
                                            metadata.forEach((val, key) => {
                                                responseTrailers.append(key, val);
                                            });
                                            if (error) {
                                                throw error;
                                            }
                                            break;
                                        }
                                        if (env.flags !== flagEnvelopeData &&
                                            env.flags !== compressedFlag) {
                                            throw new ConnectError(`protocol error: unexpected envelope flag 0x${env.flags.toString(16)}`, Code.Internal);
                                        }
                                        yield yield __await(parse(env.data));
                                    }
                                }
                                catch (e_2_1) { e_2 = { error: e_2_1 }; }
                                finally {
                                    try {
                                        if (!_d && !_a && (_b = responseMessages_1.return)) yield __await(_b.call(responseMessages_1));
                                    }
                                    finally { if (e_2) throw e_2.error; }
                                }
                            }
                            finally {
                                mux.closeStream(streamId);
                            }
                        });
                    }
                    return {
                        stream: true,
                        service: method.parent,
                        method,
                        header: responseHeaders,
                        trailer: responseTrailers,
                        message: iterate(),
                    };
                },
            });
        },
    };
}
